"use client";

/**
 * History tab — unified activity TIMELINE.
 *
 * One chronological feed (calls + texts + emails + appointments interleaved,
 * grouped by day, upcoming appointments pinned on top) instead of a grid of
 * channel boxes. Filter chips switch to a single channel's full component
 * (player, transcript, threads, pagination — all the existing machinery)
 * rendered full-width. Empty channels never take up space: their chip just
 * shows a zero.
 */

import React, { useMemo, useState } from "react";
import {
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  RefreshCw,
  PhoneOutgoing,
  PhoneIncoming,
  History,
  ChevronUp,
  Mic,
  Play,
  ListFilter,
  AlertTriangle,
} from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { LeadCallHistory, CompactAudioPlayer, DialerModeBadge, toCallRecording } from "./LeadCallHistory";
import { formatPhoneNumber } from "@/app/_module/utils/phoneUtils";
import { CallTranscriptPanel } from "./CallTranscriptPanel";
import { LeadEmailHistory } from "./LeadEmailHistory";
import { LeadSmsHistory } from "./LeadSmsHistory";
import { LeadAppointments } from "./LeadAppointments";
import { useSMS } from "@/app/(authenticated)/communication/_modules/hooks/use-hook";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import appointmentQueryKeys from "@/app/(authenticated)/appointments/_modules/services/keys";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import type { EmailHistory } from "@/app/(authenticated)/communication/_modules/types/email-history.types";
import type { CallHistoryItem } from "@/app/(authenticated)/communication/_modules/types/communication-history.types";
import { determineCallStatus } from "@/app/(authenticated)/communication/_modules/utils/transform-communication-data";
import type { PaginationMeta } from "@/app/_module/components/ui/data-pagination";

interface LeadHistoryProps {
  leadId: string;
  leadName?: string;
  leadPhone?: string;
  leadState?: string; // Lead's state for timezone display
  calls?: CallHistoryItem[];
  emails?: EmailHistory[];
  isLoadingCalls?: boolean;
  isLoadingEmails?: boolean;
  oauthConfigured?: boolean;
  onCallClick?: (call: CallHistoryItem) => void;
  onCallBack?: (phoneNumber: string) => void;
  onPlayRecording?: (recordingUrl: string) => void;
  onRefreshCalls?: () => Promise<void>;
  onEmailClick?: (email: EmailHistory) => void;
  onRefreshEmails?: () => Promise<void>; // Trigger email sync from OAuth provider
  isRefreshingEmails?: boolean; // Email sync in progress
  // Call pagination
  callPaginationMeta?: PaginationMeta;
  onCallPageChange?: (page: number) => void;
  onCallPageSizeChange?: (pageSize: number) => void;
  // Email pagination
  emailPaginationMeta?: PaginationMeta;
  onEmailPageChange?: (page: number) => void;
  onEmailPageSizeChange?: (pageSize: number) => void;
  // SMS pagination
  smsPaginationMeta?: PaginationMeta;
  smsRecords?: any[];
  isLoadingSms?: boolean;
  onSmsPageChange?: (page: number) => void;
  onSmsPageSizeChange?: (pageSize: number) => void;
  // SMS widget
  onOpenSmsWidget?: () => void;
}

type ViewKey = "all" | "calls" | "sms" | "emails" | "appointments";

interface TimelineEvent {
  key: string;
  kind: Exclude<ViewKey, "all">;
  date: Date;
  title: string;
  meta?: string;
  /** second line under the title — currently the outbound caller-ID */
  subMeta?: string;
  snippet?: string;
  badge?: { label: string; className: string };
  call?: CallHistoryItem;
  /**
   * Display status for call rows, derived via determineCallStatus — NOT the raw
   * `call_status`. Voicemails carry call_status='completed' with a real duration,
   * so the raw value renders them as "Answered". Badge and the Call-result filter
   * both read this so they can never disagree.
   */
  callResult?: string;
  /** dial method for call rows (manual | power_dialer | multi_line) */
  mode?: string;
  direction?: "inbound" | "outbound";
  /** grouped appointment status (scheduled | completed | cancelled | no_show) */
  apptStatus?: string;
}

// Colors + labels mirror LeadCallHistory's getStatusBadge so both views agree
const CALL_STATUS_BADGE: Record<string, string> = {
  answered: "bg-green-100 text-green-700",
  completed: "bg-green-100 text-green-700",
  voicemail: "bg-purple-100 text-purple-700",
  missed: "bg-red-100 text-red-600",
  "no-answer": "bg-red-100 text-red-600",
  no_answer: "bg-red-100 text-red-600",
  declined: "bg-orange-100 text-orange-700",
  failed: "bg-red-100 text-red-600",
  busy: "bg-orange-100 text-orange-700",
  canceled: "bg-gray-100 text-gray-600",
};

const CALL_STATUS_LABEL: Record<string, string> = {
  completed: "Answered",
  "no-answer": "No Answer",
  no_answer: "No Answer",
};

const APPT_STATUS_BADGE: Record<string, string> = {
  scheduled: "bg-amber-100 text-amber-700",
  rescheduled: "bg-amber-100 text-amber-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-gray-100 text-gray-500",
  no_show: "bg-red-100 text-red-600",
  pending: "bg-gray-100 text-gray-600",
};

/**
 * Collapse a DERIVED call status (from determineCallStatus, not the raw
 * call_status) into the three filterable outcomes. Passing the raw status here
 * silently breaks the Voicemail filter — voicemails arrive as 'completed' and
 * would group as "answered".
 */
const callResultGroup = (status?: string | null): string => {
  const s = (status || "").toLowerCase();
  if (s === "answered" || s === "completed") return "answered";
  if (s === "voicemail") return "voicemail";
  if (s === "no-answer" || s === "no_answer" || s === "missed") return "no_answer";
  return "other";
};

/** Collapse raw appointment statuses into four filterable buckets */
const apptStatusGroup = (status?: string | null): string => {
  const s = (status || "").toLowerCase();
  if (s === "completed") return "completed";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  if (s === "no_show") return "no_show";
  return "scheduled"; // scheduled, rescheduled, pending
};

const KIND_STYLE: Record<Exclude<ViewKey, "all">, { dot: string; icon: React.ElementType }> = {
  calls: { dot: "bg-blue-100 text-blue-600", icon: Phone },
  sms: { dot: "bg-green-100 text-green-600", icon: MessageSquare },
  emails: { dot: "bg-purple-100 text-purple-600", icon: Mail },
  appointments: { dot: "bg-amber-100 text-amber-600", icon: Calendar },
};

/**
 * True when the RECORDING is materially shorter than the CALL.
 *
 * The row header shows call length and the audio player shows recording
 * length. When those disagree — e.g. a 1m 28s call whose conference audio
 * stopped after 8s — an agent sees "1m 28s" next to a player that ends at
 * 0:08 and reasonably concludes the rest is missing or the player is broken.
 * We surface the gap instead of leaving them to guess.
 *
 * Two conditions, both required, so normal trimming is not flagged:
 *   - under 80% captured (healthy recordings run 96–102% of call length)
 *   - AND at least 10s actually missing, so a 30s call recorded at 28s stays
 *     clean rather than shouting about 2 seconds of ring time.
 */
const isPartialRecording = (call?: CallHistoryItem | null): boolean => {
  const rec = call?.recordingDuration;
  const total = call?.duration;
  if (!call?.recordingUrl || !rec || !total || rec <= 0 || total <= 0) return false;
  return rec < total * 0.8 && total - rec >= 10;
};

/** Plain-English explanation of the gap; null when there is nothing to explain. */
const partialRecordingNote = (call?: CallHistoryItem | null): string | null => {
  if (!isPartialRecording(call)) return null;
  return `Only ${fmtDuration(call!.recordingDuration!)} of this ${fmtDuration(
    call!.duration,
  )} call was recorded — the rest was never captured.`;
};

const fmtDuration = (secs: number) => {
  if (!secs || secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const titleCase = (s: string) =>
  s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const dayLabel = (d: Date) => {
  if (d.getTime() > Date.now()) return "Upcoming";
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, d.getFullYear() === new Date().getFullYear() ? "MMMM d" : "MMMM d, yyyy");
};

export const LeadHistory: React.FC<LeadHistoryProps> = function ({
  leadId,
  leadName,
  leadPhone,
  leadState,
  calls = [],
  emails = [],
  isLoadingCalls = false,
  isLoadingEmails = false,
  oauthConfigured = true,
  onCallClick,
  onCallBack,
  onPlayRecording,
  onRefreshCalls,
  onEmailClick,
  onRefreshEmails,
  isRefreshingEmails = false,
  callPaginationMeta,
  onCallPageChange,
  onCallPageSizeChange,
  emailPaginationMeta,
  onEmailPageChange,
  onEmailPageSizeChange,
  smsPaginationMeta,
  smsRecords: propsSmsRecords,
  isLoadingSms: propsIsLoadingSms,
  onSmsPageChange,
  onSmsPageSizeChange,
  onOpenSmsWidget,
}) {
  const [view, setView] = useState<ViewKey>("all");
  const { startConversation } = useSmsConversation();
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  // Timeline filters (direction spans calls/texts/emails; the rest are call-only)
  const [fType, setFType] = useState<string[]>([]);
  const [fDirection, setFDirection] = useState<string[]>([]);
  const [fResult, setFResult] = useState<string[]>([]);
  const [fMethod, setFMethod] = useState<string[]>([]);
  const [fApptStatus, setFApptStatus] = useState<string[]>([]);
  const [fRecorded, setFRecorded] = useState(false);

  const clearFilters = () => {
    setFType([]);
    setFDirection([]);
    setFResult([]);
    setFMethod([]);
    setFApptStatus([]);
    setFRecorded(false);
  };

  // Fetch SMS history (fallback if not provided via props)
  const { data: smsData, isLoading: isLoadingSms } = useSMS(
    { leadId },
    { enabled: !!leadId && !propsSmsRecords }
  );
  const smsRecords = propsSmsRecords ?? smsData?.items ?? [];
  const smsLoading = propsIsLoadingSms ?? isLoadingSms;
  const smsPagination = smsPaginationMeta ?? smsData?.meta;

  // Appointments — same query key as LeadAppointments, so the cache is shared
  // and switching to the Appointments view is instant.
  const { data: apptData, isLoading: apptLoading } = useQuery({
    queryKey: appointmentQueryKeys.byLead(leadId),
    queryFn: async () => {
      const baseUrl = getApiUrl(endpoint.appointments.getAppointments);
      const params = new URLSearchParams({
        entityType: "Lead",
        entityId: leadId,
        page: "1",
        limit: "50",
      });
      const response = await api.get({ url: `${baseUrl}?${params.toString()}`, auth: true });
      return (response as any)?.items || [];
    },
    enabled: !!leadId,
    staleTime: 5 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const appointments: any[] = Array.isArray(apptData) ? apptData : [];

  // Clicking a text in the timeline opens the SMS chat widget — same handler
  // chain as LeadSmsHistory (context first, legacy callback as fallback)
  const openSmsChat = async () => {
    const phone = leadPhone || (smsRecords.length > 0 ? smsRecords[0].phoneNumber : "");
    const name = leadName || (smsRecords.length > 0 ? smsRecords[0].contactName : undefined) || "Unknown";
    if (phone && leadId) {
      await startConversation({ leadId, leadName: name, phoneNumber: phone });
    } else if (onOpenSmsWidget) {
      onOpenSmsWidget();
    }
  };

  const counts: Record<Exclude<ViewKey, "all">, number> = {
    calls: callPaginationMeta?.totalItems ?? calls.length,
    sms: smsPagination?.totalItems ?? smsRecords.length,
    emails: emailPaginationMeta?.totalItems ?? emails.length,
    appointments: appointments.length,
  };

  const anyLoading = isLoadingCalls || isLoadingEmails || smsLoading || apptLoading;

  const events = useMemo<TimelineEvent[]>(() => {
    const out: TimelineEvent[] = [];

    for (const c of calls) {
      const d = new Date(c.callDate || c.createdAt);
      if (isNaN(d.getTime())) continue;
      const inbound = c.callType === "incoming" || c.callType === "inbound";
      // Same derivation the Communications call list uses, so a voicemail reads
      // as "Voicemail" here too instead of "Answered".
      const result = c.status
        ? determineCallStatus(c, inbound ? "inbound" : "outbound")
        : undefined;
      out.push({
        key: `call-${c.id}`,
        kind: "calls",
        date: d,
        title: inbound ? "Inbound call" : "Outbound call",
        direction: inbound ? "inbound" : "outbound",
        meta: fmtDuration(c.duration) || undefined,
        // Which of OUR numbers the call went through, on its own line so the badge
        // row stays readable. The field flips with direction: outbound we placed it
        // FROM one of ours; inbound they dialled TO one of ours. Using fromNumber on
        // an inbound row would just re-print the client's own number, which is
        // already the lead's phone.
        subMeta: inbound
          ? c.toNumber
            ? `to ${formatPhoneNumber(c.toNumber)}`
            : undefined
          : c.fromNumber
            ? `from ${formatPhoneNumber(c.fromNumber)}`
            : undefined,
        mode: c.dialerMode || "manual",
        callResult: result,
        badge: result
          ? {
              label: CALL_STATUS_LABEL[result] || titleCase(result),
              className: CALL_STATUS_BADGE[result] || "bg-gray-100 text-gray-600",
            }
          : undefined,
        call: c,
      });
    }

    for (const s of smsRecords) {
      const raw = s.timestamp || s.messageDate || s.createdAt;
      const d = new Date(raw);
      if (isNaN(d.getTime())) continue;
      const inbound = (s.direction || s.messageType) === "inbound";
      out.push({
        key: `sms-${s.id ?? d.getTime()}`,
        kind: "sms",
        date: d,
        title: inbound ? "Text received" : "Text sent",
        direction: inbound ? "inbound" : "outbound",
        snippet: s.message || s.messageContent || "",
      });
    }

    for (const e of emails) {
      const d = new Date(e.sentAt || e.createdAt);
      if (isNaN(d.getTime())) continue;
      out.push({
        key: `email-${e.id}`,
        kind: "emails",
        date: d,
        title: e.emailType === "inbound" ? "Email received" : "Email sent",
        direction: e.emailType === "inbound" ? "inbound" : "outbound",
        snippet: e.subject || "(no subject)",
      });
    }

    for (const a of appointments) {
      const d = new Date(a.startTime);
      if (isNaN(d.getTime())) continue;
      out.push({
        key: `appt-${a.id}`,
        kind: "appointments",
        date: d,
        title: a.title || "Appointment",
        apptStatus: apptStatusGroup(a.status),
        meta: format(d, "MMM d, h:mm a"),
        badge: a.status
          ? { label: titleCase(a.status), className: APPT_STATUS_BADGE[a.status?.toLowerCase()] || "bg-gray-100 text-gray-600" }
          : undefined,
      });
    }

    // Upcoming (future) first — soonest on top; then the past, newest first.
    const now = Date.now();
    const future = out.filter((e) => e.date.getTime() > now).sort((a, b) => a.date.getTime() - b.date.getTime());
    const past = out.filter((e) => e.date.getTime() <= now).sort((a, b) => b.date.getTime() - a.date.getTime());
    return [...future, ...past].slice(0, 150);
  }, [calls, smsRecords, emails, appointments]);

  const activeFilterCount =
    fType.length + fDirection.length + fResult.length + fMethod.length + fApptStatus.length + (fRecorded ? 1 : 0);

  const visibleEvents = useMemo(() => {
    if (activeFilterCount === 0) return events;
    const callAttrs = fResult.length > 0 || fMethod.length > 0 || fRecorded;
    const apptAttrs = fApptStatus.length > 0;
    // Which kinds stay visible: explicit Type picks, plus kinds implied by
    // attribute filters (checking "Answered" means calls; "Cancelled" means
    // appointments). Nothing selected that names a kind → all kinds.
    const implied = new Set<string>(fType);
    if (callAttrs) implied.add("calls");
    if (apptAttrs) implied.add("appointments");
    return events.filter((ev) => {
      if (implied.size > 0 && !implied.has(ev.kind)) return false;
      // Direction spans calls/texts/emails; appointments have none and drop
      // out while a direction filter is on
      if (fDirection.length > 0 && (!ev.direction || !fDirection.includes(ev.direction))) return false;
      if (ev.kind === "calls" && callAttrs) {
        if (fResult.length > 0 && !fResult.includes(callResultGroup(ev.callResult))) return false;
        if (fMethod.length > 0 && !fMethod.includes(ev.mode || "manual")) return false;
        if (fRecorded && !ev.call?.recordingUrl) return false;
      }
      if (ev.kind === "appointments" && apptAttrs) {
        if (!fApptStatus.includes(ev.apptStatus || "scheduled")) return false;
      }
      return true;
    });
  }, [events, fType, fDirection, fResult, fMethod, fApptStatus, fRecorded, activeFilterCount]);

  // More rows exist server-side than the pages loaded here?
  const hasMoreThanLoaded =
    (callPaginationMeta?.totalItems ?? 0) > calls.length ||
    (emailPaginationMeta?.totalItems ?? 0) > emails.length ||
    (smsPagination?.totalItems ?? 0) > smsRecords.length;

  const chips: Array<{ key: ViewKey; label: string; icon?: React.ElementType }> = [
    { key: "all", label: "All" },
    { key: "calls", label: "Calls", icon: Phone },
    { key: "sms", label: "SMS", icon: MessageSquare },
    { key: "emails", label: "Emails", icon: Mail },
    { key: "appointments", label: "Appointments", icon: Calendar },
  ];

  const channelContent: Record<Exclude<ViewKey, "all">, React.ReactNode> = {
    calls: (
      <LeadCallHistory
        leadId={leadId}
        leadName={leadName}
        calls={calls}
        isLoading={isLoadingCalls}
        onCallClick={onCallClick}
        onCallBack={onCallBack}
        onPlayRecording={onPlayRecording}
        onRefresh={onRefreshCalls}
        paginationMeta={callPaginationMeta}
        onPageChange={onCallPageChange}
        onPageSizeChange={onCallPageSizeChange}
        embedded
      />
    ),
    emails: (
      <LeadEmailHistory
        leadId={leadId}
        emails={emails}
        isLoading={isLoadingEmails || isRefreshingEmails}
        oauthConfigured={oauthConfigured}
        onEmailClick={onEmailClick}
        paginationMeta={emailPaginationMeta}
        onPageChange={onEmailPageChange}
        onPageSizeChange={onEmailPageSizeChange}
        embedded
      />
    ),
    sms: (
      <LeadSmsHistory
        leadId={leadId}
        leadName={leadName}
        leadPhone={leadPhone}
        smsRecords={smsRecords}
        isLoading={smsLoading}
        paginationMeta={smsPagination}
        onPageChange={onSmsPageChange}
        onPageSizeChange={onSmsPageSizeChange}
        onOpenSmsWidget={onOpenSmsWidget}
        embedded
      />
    ),
    appointments: (
      <LeadAppointments leadId={leadId} leadName={leadName} leadState={leadState} embedded />
    ),
  };

  // ── Timeline (All) ──────────────────────────────────────────────────────
  const renderTimeline = () => {
    if (anyLoading && events.length === 0) {
      return (
        <div className="space-y-3 p-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 animate-pulse rounded-full bg-gray-100" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (visibleEvents.length === 0) {
      const filtering = activeFilterCount > 0;
      return (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          {filtering ? <ListFilter className="h-8 w-8 text-gray-300" /> : <History className="h-8 w-8 text-gray-300" />}
          <p className="text-sm font-medium text-gray-600">
            {filtering ? "Nothing matches these filters" : "No activity yet"}
          </p>
          {filtering ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          ) : (
            <p className="max-w-xs text-xs text-gray-400">
              Calls, texts, emails and appointments for this lead will show up here as one timeline.
            </p>
          )}
        </div>
      );
    }

    let lastLabel = "";
    return (
      <div className="relative px-4 py-3">
        {/* the spine */}
        <div className="absolute bottom-3 left-[31px] top-3 w-px bg-gray-200" />
        <div className="space-y-1">
          {visibleEvents.map((ev) => {
            const label = dayLabel(ev.date);
            const showLabel = label !== lastLabel;
            lastLabel = label;
            const style = KIND_STYLE[ev.kind];
            const Icon = ev.kind === "calls"
              ? (ev.call?.callType === "incoming" || ev.call?.callType === "inbound" ? PhoneIncoming : PhoneOutgoing)
              : style.icon;
            const canExpand = ev.kind === "calls" && !!ev.call?.recordingUrl;
            const isExpanded = expandedCall === ev.key;
            const handleRow = () => {
              if (canExpand) setExpandedCall(isExpanded ? null : ev.key);
              else if (ev.kind === "sms") void openSmsChat();
              else if (ev.kind !== "calls") setView(ev.kind);
            };
            return (
              <React.Fragment key={ev.key}>
                {showLabel && (
                  <div className="relative z-10 flex items-center gap-2 pb-1 pt-3 first:pt-0">
                    <span className="ml-[24px] rounded-full border bg-white px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {label}
                    </span>
                  </div>
                )}
                <div
                  onClick={handleRow}
                  className={`group relative z-10 flex w-full items-start gap-3 rounded-lg px-1.5 py-2 text-left transition-colors ${
                    canExpand || ev.kind !== "calls" ? "cursor-pointer hover:bg-gray-50" : ""
                  }`}
                  title={
                    canExpand
                      ? isExpanded
                        ? "Hide recording"
                        : "Listen + transcript"
                      : ev.kind === "sms"
                        ? "Open chat"
                        : ev.kind !== "calls"
                          ? `Open ${ev.kind}`
                          : undefined
                  }
                >
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${style.dot}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium text-gray-800">{ev.title}</span>
                      {ev.kind === "calls" && <DialerModeBadge mode={ev.mode} />}
                      {ev.badge && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ev.badge.className}`}>
                          {ev.badge.label}
                        </span>
                      )}
                      {ev.kind === "calls" && ev.call?.recordingUrl && (
                        isPartialRecording(ev.call) ? (
                          <span
                            className="flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                            title={partialRecordingNote(ev.call) || undefined}
                          >
                            <Mic className="h-2.5 w-2.5" /> Rec {fmtDuration(ev.call.recordingDuration!)}
                          </span>
                        ) : (
                          <span className="flex items-center gap-0.5 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                            <Mic className="h-2.5 w-2.5" /> Rec
                          </span>
                        )
                      )}
                      {ev.meta && <span className="text-xs text-gray-400">{ev.meta}</span>}
                    </span>
                    {ev.subMeta && (
                      <span className="mt-0.5 block truncate text-xs text-gray-400">{ev.subMeta}</span>
                    )}
                    {ev.snippet && (
                      <span className="mt-0.5 block truncate text-xs text-gray-500">{ev.snippet}</span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-2 pt-0.5">
                    {ev.kind === "calls" &&
                      (ev.call?.callType === "incoming" || ev.call?.callType === "inbound") &&
                      onCallBack &&
                      ev.call?.phoneNumber && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCallBack(ev.call!.phoneNumber);
                          }}
                          className="h-6 px-2 text-[11px] text-green-600 hover:bg-green-50 hover:text-green-700"
                        >
                          <Phone className="mr-1 h-3 w-3" />
                          Call Back
                        </Button>
                      )}
                    <span className="text-[11px] tabular-nums text-gray-400">{format(ev.date, "h:mm a")}</span>
                    {canExpand && (
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                          isExpanded
                            ? "bg-gray-200 text-gray-600"
                            : "bg-primary/10 text-primary group-hover:bg-primary/20"
                        }`}
                        title={isExpanded ? "Hide recording" : "Play recording"}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <Play className="ml-0.5 h-3 w-3 fill-current" />
                        )}
                      </span>
                    )}
                  </span>
                </div>
                {isExpanded && ev.call?.recordingUrl && (
                  <div className="relative z-10 ml-11 mr-1 mb-2 rounded-lg border bg-gray-50/70 p-3">
                    {/*
                      Sits ABOVE the player on purpose: the agent needs to know
                      the audio is short BEFORE they hit play and start
                      wondering where the rest went.
                    */}
                    {partialRecordingNote(ev.call) && (
                      <p className="mb-2 flex items-start gap-1.5 text-[11px] leading-snug text-amber-700">
                        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                        <span>{partialRecordingNote(ev.call)}</span>
                      </p>
                    )}
                    <CompactAudioPlayer recording={toCallRecording({ ...ev.call, recordingUrl: ev.call.recordingUrl }, { leadId, leadName })} />
                    {/*
                      Transcription is bounded by the RECORDING, not the call —
                      passing call duration made a 9s clip look like a stalled
                      1m 28s job.
                    */}
                    <CallTranscriptPanel recording={toCallRecording({ ...ev.call, recordingUrl: ev.call.recordingUrl }, { leadId, leadName })} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        {hasMoreThanLoaded && (
          <p className="pb-1 pl-[44px] pt-2 text-[11px] text-gray-400">
            Showing recent activity — open a channel above for the full history.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
        {chips.map((chip) => {
          const active = view === chip.key;
          const ChipIcon = chip.icon;
          const count = chip.key === "all" ? undefined : counts[chip.key];
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setView(chip.key)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              {ChipIcon && <ChipIcon className="h-3.5 w-3.5" />}
              {chip.label}
              {count !== undefined && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
                    active ? "bg-white/20 text-white" : count > 0 ? "bg-gray-100 text-gray-600" : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeFilterCount > 0
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <ListFilter className="h-3.5 w-3.5" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[70vh] w-56 overflow-y-auto">
              <DropdownMenuLabel className="text-xs">Type</DropdownMenuLabel>
              {[
                { v: "calls", label: "Calls" },
                { v: "sms", label: "Texts" },
                { v: "emails", label: "Emails" },
                { v: "appointments", label: "Appointments" },
              ].map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.v}
                  checked={fType.includes(o.v)}
                  onCheckedChange={() => {
                    setFType((cur) => (cur.includes(o.v) ? cur.filter((x) => x !== o.v) : [...cur, o.v]));
                    if (view !== "all") setView("all");
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Direction</DropdownMenuLabel>
              {[
                { v: "inbound", label: "Inbound" },
                { v: "outbound", label: "Outbound" },
              ].map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.v}
                  checked={fDirection.includes(o.v)}
                  onCheckedChange={() => {
                    setFDirection((cur) => (cur.includes(o.v) ? cur.filter((x) => x !== o.v) : [...cur, o.v]));
                    if (view !== "all") setView("all");
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Call result</DropdownMenuLabel>
              {[
                { v: "answered", label: "Answered" },
                { v: "no_answer", label: "No Answer" },
                { v: "voicemail", label: "Voicemail" },
              ].map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.v}
                  checked={fResult.includes(o.v)}
                  onCheckedChange={() => {
                    setFResult((cur) => (cur.includes(o.v) ? cur.filter((x) => x !== o.v) : [...cur, o.v]));
                    if (view !== "all") setView("all");
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Dial method</DropdownMenuLabel>
              {[
                { v: "manual", label: "Manual" },
                { v: "power_dialer", label: "Power Dialer" },
                { v: "multi_line", label: "Multi-Line" },
              ].map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.v}
                  checked={fMethod.includes(o.v)}
                  onCheckedChange={() => {
                    setFMethod((cur) => (cur.includes(o.v) ? cur.filter((x) => x !== o.v) : [...cur, o.v]));
                    if (view !== "all") setView("all");
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Appointment status</DropdownMenuLabel>
              {[
                { v: "scheduled", label: "Scheduled" },
                { v: "completed", label: "Completed" },
                { v: "cancelled", label: "Cancelled" },
                { v: "no_show", label: "No Show" },
              ].map((o) => (
                <DropdownMenuCheckboxItem
                  key={o.v}
                  checked={fApptStatus.includes(o.v)}
                  onCheckedChange={() => {
                    setFApptStatus((cur) => (cur.includes(o.v) ? cur.filter((x) => x !== o.v) : [...cur, o.v]));
                    if (view !== "all") setView("all");
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                >
                  {o.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={fRecorded}
                onCheckedChange={() => {
                  setFRecorded((v) => !v);
                  if (view !== "all") setView("all");
                }}
                onSelect={(e) => e.preventDefault()}
                className="text-xs"
              >
                Has recording
              </DropdownMenuCheckboxItem>
              {activeFilterCount > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="justify-center text-xs text-gray-500"
                    onSelect={clearFilters}
                  >
                    Clear all
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {view === "emails" && onRefreshEmails && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRefreshEmails()}
              disabled={isRefreshingEmails}
              className="h-7 gap-1.5 px-2 text-xs text-gray-500"
              title="Sync emails from Gmail/Outlook"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshingEmails ? "animate-spin" : ""}`} />
              Sync
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="rounded-xl border bg-white">
        <ScrollArea className="h-[calc(100vh-330px)] min-h-[440px]">
          {view === "all" ? (
            renderTimeline()
          ) : (
            <div className="p-3">{channelContent[view]}</div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};
